import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Table } from "../../components/ui/Table";
import { Modal } from "../../components/ui/Modal";
import { formatFecha, formatFechaHora } from "../../lib/dates";

interface CertificadoRow {
  piloto: { id: number; nif: string; nombre: string; apellidos: string };
  seguro: { fecha_inicio?: string; fecha_fin?: string; evento_referencia?: string | null; modalidad_nombre?: string };
  cliente: { razon_social?: string };
  certificado_url?: string;
}

interface CertificadoDetail {
  piloto: {
    id: number;
    nif?: string;
    nombre: string;
    apellidos: string;
    fechaNacimiento?: string;
    email?: string;
    telefono?: string;
    direccion?: string | null;
    poblacion?: string | null;
    provincia?: string | null;
    codigoPostal?: string | null;
    fechaInicio?: string;
    fechaFin?: string;
    estado?: string;
  };
  seguro: { modalidad_nombre?: string };
  cliente: { razonSocial?: string };
  certificado_url?: string;
}

export function CertificadosPage() {
  const [filters, setFilters] = useState({
    nif: "",
    nombre: "",
    apellidos: "",
    fecha_inicio: "",
    fecha_fin: "",
  });
  const [list, setList] = useState<CertificadoRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<CertificadoDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleSearch = async () => {
    setError(null);
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filters.nif) params.nif = filters.nif;
      if (filters.nombre) params.nombre = filters.nombre;
      if (filters.apellidos) params.apellidos = filters.apellidos;
      if (filters.fecha_inicio) params.fecha_inicio = filters.fecha_inicio;
      if (filters.fecha_fin) params.fecha_fin = filters.fecha_fin;
      const res = await api.get<CertificadoRow[]>("/aseguradora/certificados", { params });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status !== 401) {
        setError(ax.response?.data?.error ?? "Error al realizar la búsqueda. Inténtalo de nuevo.");
      }
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const openCertificado = async (pilotoId: number) => {
    try {
      const res = await api.get<CertificadoDetail>(`/aseguradora/certificados/${pilotoId}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch {
      setDetail(null);
    }
  };

  const columns = [
    { key: "nif", header: "NIF", render: (r: CertificadoRow) => r.piloto?.nif ?? "-" },
    { key: "nombre", header: "Nombre completo", render: (r: CertificadoRow) => `${r.piloto?.nombre ?? ""} ${r.piloto?.apellidos ?? ""}`.trim() || "-" },
    { key: "modalidad", header: "Modalidad", render: (r: CertificadoRow) => r.seguro?.modalidad_nombre ?? "-" },
    { key: "evento", header: "Paquete", render: (r: CertificadoRow) => r.seguro?.evento_referencia?.trim() || "Sin paquete" },
    { key: "fecha_inicio", header: "Fecha inicio", render: (r: CertificadoRow) => formatFecha(r.seguro?.fecha_inicio) },
    { key: "fecha_fin", header: "Fecha fin", render: (r: CertificadoRow) => formatFecha(r.seguro?.fecha_fin) },
    { key: "organizador", header: "Organizador", render: (r: CertificadoRow) => r.cliente?.razon_social ?? "-" },
    {
      key: "ficha",
      header: "Ficha",
      render: (r: CertificadoRow) => <Button size="sm" variant="secondary" onClick={() => openCertificado(r.piloto.id)}>Ver ficha</Button>,
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Certificados</h1>

      <div className="space-y-3 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <Input
            label="NIF"
            value={filters.nif}
            onChange={(e) => setFilters((f) => ({ ...f, nif: e.target.value }))}
            placeholder="Buscar por NIF..."
            className="w-36"
          />
          <Input
            label="Nombre"
            value={filters.nombre}
            onChange={(e) => setFilters((f) => ({ ...f, nombre: e.target.value }))}
            className="w-40"
          />
          <Input
            label="Apellidos"
            value={filters.apellidos}
            onChange={(e) => setFilters((f) => ({ ...f, apellidos: e.target.value }))}
            className="w-40"
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Input
            type="date"
            label="Fecha inicio cobertura"
            value={filters.fecha_inicio}
            onChange={(e) => setFilters((f) => ({ ...f, fecha_inicio: e.target.value }))}
            className="w-40 min-w-[140px]"
          />
          <Input
            type="date"
            label="Fecha fin cobertura"
            value={filters.fecha_fin}
            onChange={(e) => setFilters((f) => ({ ...f, fecha_fin: e.target.value }))}
            className="w-40 min-w-[140px]"
          />
          <Button onClick={handleSearch} loading={loading}>
            Buscar
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Table<CertificadoRow> columns={columns} data={list} loading={loading} emptyMessage="No hay resultados. Ajusta los filtros y pulsa Buscar." />

      <Modal isOpen={detailOpen} onClose={() => { setDetailOpen(false); setDetail(null); }} title="Ficha de asegurado" footer={null}>
        {detail ? (
          <div className="space-y-4">
            <section className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <p><strong>Piloto:</strong> {detail.piloto?.nombre} {detail.piloto?.apellidos}</p>
              <p><strong>NIF:</strong> {detail.piloto?.nif ?? "-"}</p>
              <p><strong>Fecha nacimiento:</strong> {detail.piloto?.fechaNacimiento ?? "-"}</p>
              <p><strong>Email:</strong> {detail.piloto?.email ?? "-"}</p>
              <p><strong>Teléfono:</strong> {detail.piloto?.telefono ?? "-"}</p>
              <p><strong>Estado:</strong> {detail.piloto?.estado ?? "-"}</p>
              <p className="sm:col-span-2"><strong>Dirección:</strong> {[detail.piloto?.direccion, detail.piloto?.codigoPostal, detail.piloto?.poblacion, detail.piloto?.provincia].filter(Boolean).join(", ") || "-"}</p>
            </section>
            <section className="grid gap-x-6 gap-y-3 border-t border-gray-100 pt-4 sm:grid-cols-2">
              <p><strong>Modalidad:</strong> {detail.seguro?.modalidad_nombre ?? "-"}</p>
              <p><strong>Organizador:</strong> {detail.cliente?.razonSocial ?? "-"}</p>
              <p><strong>Fecha inicio:</strong> {formatFechaHora(detail.piloto?.fechaInicio)}</p>
              <p><strong>Fecha fin:</strong> {formatFechaHora(detail.piloto?.fechaFin)}</p>
            </section>
            {detail.certificado_url && (
              <a href={`/api/aseguradora/certificados/${detail.piloto?.id}/descargar`} target="_blank" rel="noreferrer" className="inline-block text-[#1A3AB8] hover:underline font-medium">Descargar PDF</a>
            )}
          </div>
        ) : (
          <div className="py-4 text-gray-500">Cargando…</div>
        )}
      </Modal>
    </div>
  );
}
